import { useCallback, useMemo } from 'react'
import { faChevronLeft, faChevronRight, faColumns3, faPlus, faSearch } from '@fortawesome/pro-light-svg-icons'
import useEditDeleteModal from '../../../../../../../../utils/hooks/useEditDeleteModal'
import ModalCECustomerProjectFlow from '../CustomerProjectFlowHeader/ModalCECustomerProjectFlow'
import ModalCreateCustomerProjectFlow from '../CustomerProjectFlowHeader/ModalCreateCustomerProjectFlow'
import TwinIcon from '../../../../../../../../baseComponents/TwinIcon'
import TwinTrans from '../../../../../../../../baseComponents/TwinTrans'
import { ButtonPrimary, ButtonSecondary } from '../../../../../../../../baseComponents/Button'
import PermissionChecker from '../../../../../../../../baseComponents/PermissionChecker'
import { getUserPermissions } from '../../../../../../../../utils/reducers/getters'
import { useNavigate, useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import { ReduxCustomerProjectTypes } from '..'
import { CustomSelectWithSearchBar } from '../../../../../../../../forms/CustomSelect'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { getBasePath } from '../../../../../../../../utils/globals/link'
import { ButtonChangeToStatusView } from '../../../../../../../../specificComponents/ButtonChangeStatusView'
import { projectTypesInversed, ProjectTypesKeys } from '@teinor/erp/types/company/customer/customerProject'
import { useParseCustomerProjectTypeByStatus } from '../../../../../../../../utils/hooks/customer/useProjectsAndOpportunitiesDict'

type CustomerProjectFlowHeaderProps = CustomerProjectFlowHeaderLogicProps & CustomerProjectFlowSelectorProps & {
    customerProjectTypeId: number
    getData: () => Promise<void>
    setView: React.Dispatch<React.SetStateAction<'default' | 'customer_projects_status_view'>>
}

const CustomerProjectFlowHeader: React.FC<CustomerProjectFlowHeaderProps> = ({ customerProjectTypeId, getData, customerProjectTypes, ProjectTypeId, setView, projectTypeStatus, ...logic }) => {
    const { handleModalCreateOpened, handleModalEditOpened, openModal, setOpenModal } = useCustomerProjectFlowHeaderLogic()

    return (
        <div className='table_header flex justify-between items-center mb-22 flex-wrap gap-10'>
            <CustomerProjectFlowSelector customerProjectTypes={customerProjectTypes} projectTypeStatus={projectTypeStatus} ProjectTypeId={ProjectTypeId} {...{ ...logic }} />
            <div className='flex gap-20 flex-wrap-reverse'>
                <ButtonChangeToStatusView onClick={() => setView('default')} />
                <PermissionChecker userPermissions={getUserPermissions('customer.customerProjectType')} permission={'update'}>
                    <ButtonSecondary onClick={handleModalEditOpened} className='h-34'>
                        <TwinIcon icon={faColumns3} />
                        <span className='load_columns_button_text ml-8'>
                            <TwinTrans transKey='manageStatuses'>Gestionar estados</TwinTrans>
                        </span>
                    </ButtonSecondary>
                </PermissionChecker>
                <PermissionChecker userPermissions={getUserPermissions('customer.projects.main')} permission={'create'}>
                    <ButtonPrimary onClick={handleModalCreateOpened}>
                        <TwinIcon icon={faPlus} className='mr-8' />
                        {(projectTypeStatus === projectTypesInversed['opportunity']) ?
                            <TwinTrans transKey='newOpportunity'>Nueva oportunidad</TwinTrans>
                            : <TwinTrans transKey='newProject'>Nuevo proyecto</TwinTrans>
                        }
                    </ButtonPrimary>
                </PermissionChecker>
                {openModal?.type === 'create' ? <ModalCreateCustomerProjectFlow customerProjectTypeId={customerProjectTypeId} setOpened={() => setOpenModal(null)} onSubmit={getData} projectType={projectTypeStatus} /> : null}
                {openModal?.type === 'edit' ? <ModalCECustomerProjectFlow customerProjectTypeId={customerProjectTypeId} setOpened={() => setOpenModal(null)} onSubmit={getData} projectType={projectTypeStatus} /> : null}
            </div>
        </div>
    )
}

interface CustomerProjectFlowHeaderLogicProps {}

const useCustomerProjectFlowHeaderLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()

    const handleModalCreateOpened = useCallback(() => {
            setOpenModal({
                type: 'create',
                allRowData: {}
            })
        }, [setOpenModal])
    const handleModalEditOpened = useCallback(() => {
            setOpenModal({
                type: 'edit',
                allRowData: {}
            })
        }, [setOpenModal])

    return { handleModalCreateOpened, handleModalEditOpened, openModal, setOpenModal }
}

interface CustomerProjectFlowSelectorProps extends CustomerProjectFlowSelectorLogicProps { }

const CustomerProjectFlowSelector: React.FC<CustomerProjectFlowSelectorProps> = ({ customerProjectTypes, ProjectTypeId, ...rest }) => {
    const dictCustomerProjectTypes = dictionaryComplexFromJsonArr(customerProjectTypes || [])
    const { linkNextCustomerProjectType, linkPrevCustomerProjectType, onSelectCustomerProjectType, parsedCustomerProjectTypes } = useCustomerProjectFlowSelectorLogic({ customerProjectTypes: dictCustomerProjectTypes, ProjectTypeId, ...rest })
    return (
        <div className='flex items-center'>
            <Link className='cursor-pointer hover:text-green-21 flex items-center' to={linkPrevCustomerProjectType}><TwinIcon className='h-20' icon={faChevronLeft} /></Link>
            <Link className='cursor-pointer hover:text-green-21 ml-10 flex items-center' to={linkNextCustomerProjectType}><TwinIcon className='h-20' icon={faChevronRight} /></Link>
            <div className='toolbar-label  text-left text-gray-51 capitalize mx-10 mt-1'>
                <h1 className='text-gray-51 text-18'>
                    <TwinTrans transKey={'board'}>Tablero</TwinTrans>:  {dictCustomerProjectTypes?.[ProjectTypeId]?.name}
                </h1>
            </div>
            <CustomSelectWithSearchBar className='customer_project_flow_selector mr-24 ' label='' items={parsedCustomerProjectTypes} onChange={onSelectCustomerProjectType} icon={faSearch} fieldName='name' />
        </div>
    )
}
interface CustomerProjectFlowSelectorLogicProps extends ReduxCustomerProjectTypes {
    ProjectTypeId: number
    projectTypeStatus: ProjectTypesKeys
}

const useCustomerProjectFlowSelectorLogic = ({ customerProjectTypes, ProjectTypeId, projectTypeStatus }: CustomerProjectFlowSelectorLogicProps) => {
    const navigate = useNavigate()
    const { myParsedCustomerProjectTypes } = useParseCustomerProjectTypeByStatus({ customerProjectTypes, projectTypeStatus: 'opportunity' })
    const positionsCPT = useMemo(() => {
        let result: TwinDictionary = {
            byPos: {},
            byId: {},
            lastPos: 0
        }
        let pos = 0
        for (const key in myParsedCustomerProjectTypes) {
            result.byPos[pos] = key
            result.byId[key] = pos
            pos++
        }
        result.lastPos = pos - 1
        return result
    }, [myParsedCustomerProjectTypes])

    const basePathDict: Record<ProjectTypesKeys, string> = {
        0: 'opportunitiesStatusFlow',
        1: 'projectStatusFlow'
    }

    const location = useLocation()
    const basePath = getBasePath(location.pathname, basePathDict[projectTypeStatus])
    const nextCustomerProjectType = useCallback(() => {
        const posId = positionsCPT.byId[ProjectTypeId]
        const parsed = positionsCPT.byPos[posId + 1] || positionsCPT.byPos[0]
        return `${basePath}/${parsed}/main`
    }, [positionsCPT, ProjectTypeId, basePath])

    const prevCustomerProjectTyp = useCallback(() => {
        const posId = positionsCPT.byId[ProjectTypeId]
        const parsed = positionsCPT.byPos[posId - 1] || positionsCPT.byPos[positionsCPT.lastPos]
        return `${basePath}/${parsed}/main`
    }, [positionsCPT, ProjectTypeId, basePath])

    const onSelectCustomerProjectType = useCallback((id: number) => {
        navigate(`${basePath}/${id}/main`)
    }, [navigate, basePath])

    return { linkNextCustomerProjectType: nextCustomerProjectType(), linkPrevCustomerProjectType: prevCustomerProjectTyp(), onSelectCustomerProjectType, parsedCustomerProjectTypes: myParsedCustomerProjectTypes }
}


export default CustomerProjectFlowHeader